import { useState, useRef, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, CheckCircle2, Truck, Building2, MapPin, Calendar, User } from 'lucide-react';
import SignaturePad from '@/components/shared/SignaturePad';
import DisclosureText from './DisclosureText';
import type { EstimateLine } from './PickupDetailsStep';
import type { WorkOrder } from '@/types/models';

interface Props {
  wo: WorkOrder;
  siteName: string;
  categories: { id: string; name: string }[];
  estimateLines: EstimateLine[];
  photos: string[];
  notes: string;
  onBack: () => void;
  onComplete: (data: { signerName: string; signature: string; timestamp: string; timezone: string }) => void;
  isSubmitting: boolean;
}

export default function PickupReviewStep({
  wo, siteName, categories, estimateLines, photos, notes, onBack, onComplete, isSubmitting,
}: Props) {
  const [accepted, setAccepted] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [signature, setSignature] = useState<string | null>(null);
  const [hasScrolledToEnd, setHasScrolledToEnd] = useState(false);

  const handleDisclosureScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 10) {
      setHasScrolledToEnd(true);
    }
  }, []);

  const canComplete = accepted && signerName.trim().length > 0 && !!signature;

  function handleComplete() {
    if (!canComplete || !signature) return;
    const now = new Date();
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    onComplete({ signerName, signature, timestamp: now.toISOString(), timezone });
  }

  return (
    <div className="space-y-6">
      {/* WO Header Summary */}
      <Card>
        <CardContent className="p-6">
          <h3 className="text-sm font-semibold text-foreground mb-3">Work Order Summary</h3>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 text-sm">
            <div className="flex items-center gap-2">
              {wo.work_order_type === 'pickup' ? <Truck className="h-4 w-4 text-primary" /> : <Building2 className="h-4 w-4 text-primary" />}
              <span className="text-muted-foreground">Type:</span>
              <span className="font-medium capitalize">{wo.work_order_type}</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-primary" />
              <span className="text-muted-foreground">Site:</span>
              <span className="font-medium">{siteName}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-primary" />
              <span className="text-muted-foreground">Date:</span>
              <span className="font-medium">{wo.scheduled_date}</span>
            </div>
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-primary" />
              <span className="text-muted-foreground">Operator:</span>
              <span className="font-medium">{wo.assigned_operator || '—'}</span>
            </div>
          </div>
          <div className="mt-3 text-sm">
            <span className="text-muted-foreground">Customer:</span>{' '}
            <span className="font-medium">{wo.customer_name} · {wo.company}</span>
          </div>
        </CardContent>
      </Card>

      {/* Estimates Table */}
      {estimateLines.length > 0 && (
        <Card>
          <CardContent className="p-6">
            <h3 className="text-sm font-semibold text-foreground mb-3">Pre-Intake Estimates</h3>
            <div className="border rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">Category</th>
                    <th className="text-right px-4 py-2 font-medium text-muted-foreground">Amount</th>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">Unit</th>
                  </tr>
                </thead>
                <tbody>
                  {estimateLines.map((est, i) => {
                    const cat = categories.find((c) => c.id === est.categoryId);
                    return (
                      <tr key={i} className="border-t">
                        <td className="px-4 py-2">{cat?.name || '—'}</td>
                        <td className="px-4 py-2 text-right">{est.value}</td>
                        <td className="px-4 py-2">
                          <Badge variant="secondary" className="text-xs">{est.measureType === 'qty' ? 'Qty' : 'Lbs'}</Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Photos */}
      {photos.length > 0 && (
        <Card>
          <CardContent className="p-6">
            <h3 className="text-sm font-semibold text-foreground mb-3">Pickup Photos ({photos.length})</h3>
            <div className="flex flex-wrap gap-2">
              {photos.map((p, i) => (
                <img key={i} src={p} alt={`Photo ${i + 1}`} className="h-20 w-20 rounded-md object-cover border" />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Notes */}
      <Card>
        <CardContent className="p-6">
          <h3 className="text-sm font-semibold text-foreground mb-2">Pickup Notes</h3>
          <p className="text-sm text-muted-foreground whitespace-pre-wrap">{notes}</p>
        </CardContent>
      </Card>

      {/* Disclosure */}
      <Card>
        <CardContent className="p-6">
          <h3 className="text-sm font-semibold text-foreground mb-3">Terms & Conditions</h3>
          <div className="h-64 overflow-y-auto border rounded-md p-4" onScroll={handleDisclosureScroll}>
            <DisclosureText />
          </div>
          {hasScrolledToEnd && (
            <div className="mt-4 flex items-start gap-2 animate-in fade-in slide-in-from-top-2 duration-300">
              <Checkbox
                id="accept-disclosure"
                checked={accepted}
                onCheckedChange={(v) => setAccepted(v === true)}
              />
              <Label htmlFor="accept-disclosure" className="text-sm leading-tight cursor-pointer">
                I have read and agree to the terms above
              </Label>
            </div>
          )}
          {!hasScrolledToEnd && (
            <p className="mt-3 text-xs text-muted-foreground italic">Please scroll to the end of the terms to continue</p>
          )}
        </CardContent>
      </Card>

      {/* Signature */}
      <Card className={accepted ? '' : 'opacity-60 pointer-events-none'}>
        <CardContent className="p-6 space-y-4">
          <div>
            <Label className="text-xs text-muted-foreground">Signer Name <span className="text-destructive">*</span></Label>
            <Input value={signerName} onChange={(e) => setSignerName(e.target.value)} placeholder="Full name of person signing" />
          </div>
          <SignaturePad onSignatureChange={setSignature} label="Customer Signature *" />
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex items-center gap-3 pb-8">
        <Button variant="outline" onClick={onBack} className="gap-2">
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <Button onClick={handleComplete} disabled={!canComplete || isSubmitting} className="gap-2">
          <CheckCircle2 className="h-4 w-4" /> {isSubmitting ? 'Submitting…' : 'Complete Pickup'}
        </Button>
      </div>
    </div>
  );
}
